// services
import getDriveKeys from "services/orchid-drive/getDriveKeys";
import getDriveSnapshot from "services/orchid-drive/getDriveSnapshot";
// utils
import { isDriveEntryADirectory } from "contexts/orchid-drive";
import downloadFileChunk from "utils/downloadFileChunk";
import getMIMETypeByFileName from "utils/getMIMETypeByFileName";
import getSumInMap from "./getSumInMap";
import { getEntryMetadata } from "./getEntryMetadata";
import { acquireEntryKeys } from "./acquireEntryKeys";
import { orchidDecryptText } from "./orchidDecryptText";
import { isDriveDirectoryNotifyAvailable } from "utils/isDriveDirectoryNotifyAvailable";
import { isDriveFileNotifyAvailable } from "utils/isDriveFileNotifyAvailable";
// types
import type { SelectedItem } from "..";
import type { OrchidDriveFile } from "contexts/orchid-drive/types";

/**
 * message to be used when there is a fetch abort error
 */
export const ABORT_NAME = "AbortError";

type DownloadedFile = { relativePath: string; name: string; blob: Blob };

type DirMetadata = {
  entryId: string;
  relativePath: string;
  storageDrive: StorageDrive;
  patient?: PatientChartContact;
};

// path -> { name, blob }
type DownloadedMap = {
  directories: DirMetadata[];
  files: DownloadedFile[];
};

// iter should yield Uint8Array
function getBlob(chunks: Uint8Array[], mimeType: string) {
  let chunksAll = new Uint8Array(
    chunks.reduce((accum, curr) => accum + curr.byteLength, 0),
  );

  // concatenate chunks into single Uint8Array
  let position = 0;
  for (let chunk of chunks) {
    chunksAll.set(chunk, position); // (4.2)
    position += chunk.length;
  }

  // it is necessary to create a new blob object with mime-type explicitly set for all browsers except Chrome, but it works for Chrome too.
  const newBlob = new Blob([chunksAll], {
    type: mimeType,
  });

  return newBlob;
}

export function hexDecode(hexEncodedData: string) {
  const arrBuf = new ArrayBuffer(hexEncodedData.length / 2);
  const buffer = new Uint8Array(arrBuf);
  for (let i = 0; i < hexEncodedData.length / 2; ++i) {
    buffer.set([parseInt(hexEncodedData.substr(i * 2, 2), 16)], i);
  }
  return buffer;
}

export async function orchidDecryptBinary(
  encryptedData: Uint8Array,
  cipherKey: CryptoKey,
) {
  const iv = encryptedData.slice(0, 12);
  const data = encryptedData.slice(12);
  const clearData = await window.crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    cipherKey,
    data,
  );
  return new Uint8Array(clearData);
}

function orchidDecodeKey(hex_encoded_key) {
  const raw_data = hexDecode(hex_encoded_key);
  return window.crypto.subtle.importKey("raw", raw_data, "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
}

async function downloadChunk(
  entryId: string,
  chunkNum: number,
  dataCipherKey: CryptoKey,
  fetchOptions?: Omit<RequestInit, "credentials" | "headers">,
  onDownloadCallback?: (arg: {
    chunkNum: number;
    receivedLength: number;
  }) => void,
) {
  const response = await downloadFileChunk(
    `/api/drive_data/file_data/${entryId}/${chunkNum}`,
    fetchOptions,
    ({ receivedLength }) =>
      onDownloadCallback && onDownloadCallback({ chunkNum, receivedLength }),
  );
  return orchidDecryptBinary(response, dataCipherKey);
}

async function downloadChunks({
  entryId,
  totalChunkCount,
  dataCipherKey,
  fetchOptions,
  onDownloadCallback,
}: {
  entryId: string;
  totalChunkCount: number;
  dataCipherKey: CryptoKey;
  fetchOptions?: Omit<RequestInit, "credentials" | "headers">;
  onDownloadCallback?: (arg: {
    receivedLength: number;
    contentLength?: number;
  }) => void;
}) {
  const promises: ReturnType<typeof downloadChunk>[] = [];
  const chunksReceivedLength = Array(totalChunkCount).fill(0);
  for (let chunkNum = 0; chunkNum < totalChunkCount; ++chunkNum) {
    promises.push(
      downloadChunk(
        entryId,
        chunkNum,
        dataCipherKey,
        fetchOptions,
        ({ chunkNum, receivedLength }) => {
          chunksReceivedLength[chunkNum] = receivedLength;

          return (
            onDownloadCallback &&
            onDownloadCallback({
              receivedLength: chunksReceivedLength.reduce((a, b) => a + b, 0),
            })
          );
        },
      ),
    );
  }
  return Promise.all(promises);
}

async function getFileBlob({
  entryId,
  totalChunkCount,
  dataCipherKey,
  mimeType,
  fetchOptions,
  onDownloadCallback,
}: {
  entryId: string;
  totalChunkCount: number;
  dataCipherKey: CryptoKey;
  mimeType: string;
  fetchOptions?: Omit<RequestInit, "credentials" | "headers">;
  onDownloadCallback?: (arg: {
    receivedLength: number;
    entryId: string;
  }) => void;
}) {
  try {
    const chunks = await downloadChunks({
      entryId,
      totalChunkCount,
      dataCipherKey,
      fetchOptions,
      onDownloadCallback: ({ receivedLength }) =>
        onDownloadCallback &&
        onDownloadCallback({
          entryId,
          receivedLength,
        }),
    });
    if (!chunks) {
      throw new Error("chunk is empty");
    }
    return getBlob(chunks, mimeType);
  } catch (error) {
    throw error;
  }
}

export async function acquireEntryKeysRequest(entryIds: string[]) {
  try {
    const data = await getDriveKeys({ entry_ids: entryIds });
    if (data.failed_entries.length > 0) {
      console.error(
        "Failed to retrieve some of the drive entry encryption keys: ",
        data.failed_entries,
      );
      throw new Error(
        "failed to retrieve some of the drive entry encryption keys",
      );
    }

    const entryCipherKeys: Record<
      string,
      {
        entryId: string;
        dataCipherKey: CryptoKey;
        metaDataCipherKey: CryptoKey;
      }
    > = {};

    for (const entryKey of data.key_entries) {
      entryCipherKeys[entryKey.entry_id] = {
        entryId: entryKey.entry_id,
        dataCipherKey: await orchidDecodeKey(entryKey.data_cipher_key),
        metaDataCipherKey: await orchidDecodeKey(entryKey.meta_data_cipher_key),
      };
    }

    return entryCipherKeys;
  } catch (error) {
    console.error(error);
    throw new Error("acquireEntryKeysRequest");
  }
}

async function resolveMetadata({
  entryId,
  relativePath,
  storageDrive,
  patient,
}: {
  entryId: string;
  relativePath: string;
  storageDrive: StorageDrive;
  patient?: PatientChartContact;
}) {
  try {
    const fileMetadata: {
      size: number;
      entryId: string;
      relativePath: string;
    }[] = [];
    const dirMetadata: DirMetadata[] = [
      { entryId, relativePath, storageDrive, patient },
    ];

    for (let index = 0; index < dirMetadata.length; index++) {
      const currentDir = dirMetadata[index];

      if (currentDir) {
        const [{ directories: dirs }, { [currentDir.entryId]: cipherKeys }] =
          await Promise.all([
            getEntryMetadata([currentDir.entryId]),
            acquireEntryKeys([currentDir.entryId]),
          ]);

        const metadata = dirs[currentDir.entryId];

        if (!metadata) {
          throw new Error("cant find the dir info");
        }
        const name = await orchidDecryptText(
          metadata.name_encrypted,
          cipherKeys.metaDataCipherKey,
        );

        const contentMetaData = await getDriveSnapshot(
          {
            directory_id: currentDir.entryId,
          },
          storageDrive,
          patient?.orchid_user_id,
        );
        const resolvedDirPath =
          (!currentDir.relativePath ? "" : `${currentDir.relativePath}/`) +
          name;
        dirMetadata[index].relativePath = resolvedDirPath;

        for (const file of contentMetaData.files) {
          if (isDriveFileNotifyAvailable(file)) {
            fileMetadata.push({
              entryId: file.file_data_id,
              relativePath: resolvedDirPath,
              size: file.uploaded_size,
            });
          }
        }

        for (const dir of contentMetaData.directories) {
          if (isDriveDirectoryNotifyAvailable(dir)) {
            dirMetadata.push({
              entryId: dir.directory_id,
              relativePath: resolvedDirPath,
              storageDrive,
              patient,
            });
          }
        }
      }
    }

    return {
      files: fileMetadata,
      directories: dirMetadata,
    };
  } catch (error) {
    throw error;
  }
}

async function downloadOrchidDriveFile({
  entryId,
  relativePath,
  fetchOptions,
  onDownloadCallback,
  metadata,
  name,
  dataCipherKey,
}: {
  entryId: string;
  relativePath: string;
  fetchOptions?: Omit<RequestInit, "credentials" | "headers">;
  onDownloadCallback?: (arg: {
    receivedLength: number;
    entryId: string;
  }) => void;
  metadata: OrchidDriveFileNotify;
  name: string;
  dataCipherKey: CryptoKey;
}): Promise<DownloadedFile> {
  try {
    const totalChunkCount = (metadata.last_uploaded_chunk_num ?? 0) + 1;

    return {
      relativePath,
      name,
      blob: await getFileBlob({
        entryId,
        totalChunkCount,
        dataCipherKey: dataCipherKey,
        mimeType: metadata.mime_type || getMIMETypeByFileName(name),
        fetchOptions,
        onDownloadCallback,
      }),
    };
  } catch (error) {
    throw error;
  }
}

export default async function downloadOrchidDriveFiles({
  entries,
  storageDrive,
  patient,
  fetchOptions,
  onDownloadCallback,
}: {
  entries: SelectedItem[];
  storageDrive: StorageDrive;
  patient?: PatientChartContact;
  fetchOptions?: Omit<RequestInit, "credentials" | "headers">;
  onDownloadCallback?: (arg: {
    receivedLength?: number;
    contentLength?: number;
  }) => void;
}): Promise<DownloadedMap> {
  const res: DownloadedMap = { files: [], directories: [] };

  const filePromises: Omit<
    Parameters<typeof downloadOrchidDriveFile>[0],
    | "metadata"
    | "name"
    | "dataCipherKey"
    | "fetchOptions"
    | "onDownloadCallback"
  >[] = [];

  let totalSize = 0;

  // get metadata
  await Promise.all(
    entries.map(async (el) => {
      if (isDriveEntryADirectory(el.guid)) {
        const { files, directories } = await resolveMetadata({
          entryId: el.guid,
          relativePath: "",
          storageDrive,
          patient,
        });

        res.directories.push(...directories);

        filePromises.push(
          ...files.map((item) => {
            totalSize += item.size;
            return {
              entryId: item.entryId,
              relativePath: item.relativePath,
            };
          }),
        );
      } else {
        filePromises.push({
          entryId: el.guid,
          relativePath: "",
        });
        totalSize += (el as OrchidDriveFile).uploadedSize;
      }
    }),
  );

  if (onDownloadCallback) {
    onDownloadCallback({ contentLength: totalSize });
  }
  // resolve file metadata
  const nameMap: Record<string, string> = {};
  const fileIds = filePromises.map((el) => el.entryId);
  const [{ files: filesMap }, entryKeys] = await Promise.all([
    getEntryMetadata(fileIds),
    acquireEntryKeys(fileIds),
  ]);

  await Promise.all(
    Object.keys(filesMap).map(async (el) => {
      const metadata = filesMap[el];

      if (!metadata) {
        throw new Error("cant find the dir info");
      }
      const name = await orchidDecryptText(
        metadata.name_encrypted,
        entryKeys[el].metaDataCipherKey,
      );

      nameMap[el] = name;

      return;
    }),
  );

  const fileReceivedLength: Record<string, number> = {};
  // res.directories = await Promise.all(dirPromises);
  res.files = await Promise.all(
    filePromises.map((el) =>
      downloadOrchidDriveFile({
        ...el,
        fetchOptions,
        onDownloadCallback: ({ entryId, receivedLength }) => {
          fileReceivedLength[entryId] = receivedLength;
          return (
            onDownloadCallback &&
            onDownloadCallback({
              receivedLength: getSumInMap(fileReceivedLength),
            })
          );
        },
        name: nameMap[el.entryId],
        metadata: filesMap[el.entryId],
        dataCipherKey: entryKeys[el.entryId].dataCipherKey,
      }),
    ),
  );

  return res;
}
